
import * as React from 'react';
import { IContextualMenuItem } from 'office-ui-fabric-react/lib/ContextualMenu';
import { ICommandBarItemProps } from 'office-ui-fabric-react/lib/CommandBar';
import { withCurrentItemSet } from '../../decorators/selectors';
import { ICommand } from '../../../models/commands/ICommand';
import { Facet } from '../../../models/Facet';
import { SetPreferredLayoutAction } from '../../actions/setPreferredLayout/SetPreferredLayoutAction';
import { CommandSet, ICommandSetStateProps } from '../../commandSet/CommandSet';
import { Action } from '../../action/Action';
import { hasLayoutFacet } from '../../../facets/ScopeFacets';
import { WithItemsView, IWithItemsViewStateProps, IEvaluatedItemsViewLayoutHandler } from '../../itemsView/ItemsViewBeta';
import { commandBarItemDefaultRender, ICommandBarItemRenderProps } from '../commandBarItemDefaultRender';
import { IComponentAsProps } from '@uifabric/utilities/lib/IComponentAs';

interface ICurrentLayoutActionProps {
  itemKey: string | undefined;
}

/**
 * Action component used to show the 'current' layout in the switchLayoutCommand.
 * Will not actually be executed.
 */
class ItemCurrentLayoutAction extends React.PureComponent<ICurrentLayoutActionProps> {
  public render(): JSX.Element {
    const { itemKey } = this.props;

    return (
      <WithItemsView
        itemKey={ itemKey }
      >
        { ({ layoutHandlers, itemsScopeItemKey, itemCache }: IWithItemsViewStateProps) => {
          const hasLayout = itemCache.demandItemFacet(hasLayoutFacet, itemsScopeItemKey);

          if (!hasLayout) {
            return (
              <Action />
            );
          }

          for (const {
            layout,
            key: layoutHandlerKey
          } of layoutHandlers) {

            if (layout) {
              return (
                <SetPreferredLayoutAction layoutHandlerKey={ layoutHandlerKey } />
              );
            }
          }

          return (
            <Action />
          );
        } }
      </WithItemsView>
    );
  }
}

const CurrentLayoutAction = withCurrentItemSet()(ItemCurrentLayoutAction);

type ILayoutCommandButtonProps = IComponentAsProps<ICommandBarItemRenderProps> & {
  itemKey: string | undefined;
};

class ItemSetPreferredLayoutCommandButton extends React.PureComponent<ILayoutCommandButtonProps> {
  public render(): JSX.Element {
    const {
      itemKey,
      item: { // tslint:disable-line:no-use-before-declare
        subMenuProps,
        ...item
      },
      defaultRender: DefaultRender,
      ...buttonProps
    } = this.props;

    return (
      <WithItemsView
        itemKey={ itemKey }
      >
        { ({ layoutHandlers }: IWithItemsViewStateProps) => {
          let commands: IContextualMenuItem[] = [];

          let currentLayoutCommand: IEvaluatedItemsViewLayoutHandler['command'] | undefined;

          for (const {
            layout,
            command,
            key: layoutHandlerKey
          } of layoutHandlers) {

            if (layout && !currentLayoutCommand) {
              // The handler with the first valid layout is the active layout.
              // Its command data should be used for the 'root' command, but it will not be executed.
              currentLayoutCommand = command;
            }

            commands.push(createSetPreferredLayoutCommand({
              layoutHandlerKey,
              command
            }));
          }

          return (
            <CommandSet commands={ commands }>
              { ({ menuItems }: ICommandSetStateProps) => {
                return DefaultRender ? (
                  <DefaultRender
                    { ...buttonProps }
                    item={
                      {
                        ...item,
                        ...(currentLayoutCommand || {}),
                        subMenuProps: menuItems.length > 1 ? {
                          // Merge the menu props with any items supplemented onto the original command.
                          // This can happen if a control handler 'piggy-backs' other related commands onto the
                          // menu, such as SharePoint's view selectors or OneDrive's sort commands.
                          ...(subMenuProps || {}),
                          items: [
                            ...menuItems,
                            ...(subMenuProps && subMenuProps.items || [])
                          ]
                        } : undefined,
                        iconOnly: true,
                        checked: false,
                        disabled: menuItems.length <= 1
                      }
                    }
                  />
                ) : null;
              } }
            </CommandSet>
          );
        } }
      </WithItemsView>
    );
  }
}

const SetPreferredLayoutCommandButton = withCurrentItemSet()(ItemSetPreferredLayoutCommandButton);

export const layoutCommand: ICommand = {
  key: new Facet('layout').id,
  automationId: 'layoutCommand',
  action: <CurrentLayoutAction />,
  onRender: (item: ICommandBarItemProps): JSX.Element => {
    return (
      <SetPreferredLayoutCommandButton
        item={ item }
        defaultRender={ commandBarItemDefaultRender }
      />
    );
  }
};

function createSetPreferredLayoutCommand(props: {
  layoutHandlerKey: string;
  command: IEvaluatedItemsViewLayoutHandler['command'];
}): IContextualMenuItem {
  const {
    layoutHandlerKey,
    command
  } = props;

  return {
    key: layoutHandlerKey,
    ...command,
    action: (
      <SetPreferredLayoutAction layoutHandlerKey={ layoutHandlerKey } />
    ),
    canCheck: true
  };
}
